// CLI v0.8 — `grid use [<slug>]`.
//
// Switches the persisted active org slug (`~/.cloudgrid/config.yaml`) after
// verifying the user has a membership in the target org. With no argument,
// reads the current active org and prints it. The org-switch path is the
// validated equivalent of editing the config file by hand.
//
// Spec v17 §23 voice: plain, no exclamation marks, no marketing tone.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { getActiveOrg, setActiveOrgSlug, type ActiveOrg } from '../active-org.js';
import { handleV2Error } from '../handle-error.js';
import { Output } from '../output.js';

export interface RunUseOpts {
  client: IV2Client;
  slug?: string;
  json?: boolean;
}

export async function runUse(opts: RunUseOpts): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const active: ActiveOrg = opts.slug
    ? await getActiveOrg(opts.client, { override: opts.slug })
    : await getActiveOrg(opts.client);

  if (opts.slug) {
    setActiveOrgSlug(active.slug);
  }

  out.json(() => ({
    active_org_slug: active.slug,
    active_org: { org_id: active.org_id, slug: active.slug, name: active.name },
  }));
  out.human(() => `${opts.slug ? '✓ ' : ''}Active grid: ${active.slug} (${active.name})`);
}

export const useCommand = new Command('use')
  .description('Set the active grid for subsequent commands (or show it when called with no arg)')
  .argument('[slug]', 'Grid slug to switch to')
  .option('--json', 'JSON output')
  .action(async (slug: string | undefined, opts: { json?: boolean }) => {
    try {
      const client = await getV2Client();
      await runUse({ client, slug, json: opts.json });
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });
